import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useFilestoreFiles, useUploadToFilestore, useDeleteFilestoreFile, useUpdateFilestoreFile, downloadFilestoreFile } from '../../api/filestore';
import { useQueryClient } from '@tanstack/react-query';
import { FilestoreFile } from '../../api/filestore';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDeleteModal from '../ConfirmDeleteModal';

const FileManager: React.FC = () => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ id: number; name: string } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { files, isLoading, error } = useFilestoreFiles();
  const uploadMutation = useUploadToFilestore();
  const deleteMutation = useDeleteFilestoreFile();
  const updateMutation = useUpdateFilestoreFile();

  const currentUser = user?.username;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      setUploadError(null);
      try {
        await uploadMutation.mutateAsync({ file: selectedFiles[0], uploadedFrom: 'file_manager' });
        // Clear the input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err) {
        setUploadError('Failed to upload file');
        console.error('Upload error:', err);
      }
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['filestore'] });
  };

  const handleDelete = (fileId: number, filename: string) => {
    setFileToDelete({ id: fileId, name: filename });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete) {
      try {
        await deleteMutation.mutateAsync(fileToDelete.id);
        setDeleteConfirmOpen(false);
        setFileToDelete(null);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const handleTogglePublic = async (fileId: number, isPublic: boolean) => {
    try {
      await updateMutation.mutateAsync({ fileId, isPublic: !isPublic });
    } catch (err) {
      console.error('Error updating file visibility:', err);
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    try {
      await downloadFilestoreFile(fileId, filename);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load files: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon />
          File Manager
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh files">
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleUploadClick}
            disabled={uploadMutation.isPending}
          >
            Upload File
          </Button>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Shapefiles must be uploaded as a .zip archive.
      </Typography>

      {/* Upload Error */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        /* Files List */
        <Paper sx={{ overflow: 'hidden' }}>
          {files.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <StorageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No files found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload files or browse your existing files to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
              >
                Upload Your First File
              </Button>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Filename
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Size
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Upload Date
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Visibility
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Uploaded By
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file: FilestoreFile) => (
                    <tr key={file.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.original_filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.content_type}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2">
                          {formatFileSize(file.file_size)}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2">
                          {formatDate(file.upload_date)}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={file.is_public ? 'Public - visible to all users' : 'Private - only visible to you'}>
                            {file.is_public ? (
                              <PublicIcon fontSize="small" sx={{ fontSize: 14 }} color="success" />
                            ) : (
                              <PrivateIcon fontSize="small" sx={{ fontSize: 14 }} color="disabled" />
                            )}
                          </Tooltip>
                          <Switch
                            size="small"
                            checked={file.is_public}
                            onChange={() => handleTogglePublic(file.id, file.is_public)}
                            disabled={updateMutation.isPending}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              color: file.is_public ? 'success.main' : 'text.secondary',
                              fontWeight: file.is_public ? 600 : 400,
                              minWidth: 45
                            }}
                          >
                            {file.is_public ? 'Public' : 'Private'}
                          </Typography>
                        </Box>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2">
                          {file.username}
                          {file.is_public && currentUser && file.username !== currentUser && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', ml: 1 }}>
                              (public)
                            </Typography>
                          )}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Download file">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(file.id, file.original_filename)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {currentUser && file.username === currentUser && (
                            <Tooltip title="Delete file">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(file.id, file.original_filename)}
                                disabled={deleteMutation.isPending}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Paper>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        fileName={fileToDelete?.name || ''}
        isLoading={deleteMutation.isPending}
      />
    </Box>
  );
};

export default FileManager;
